import '@eeacms/volto-tabs-block/less/edit.less';
import '@eeacms/volto-tabs-block/less/tabs.less';

import React from 'react';
import { BlockStyleWrapperEdit } from '@eeacms/volto-block-style/BlockStyleWrapper';
import { StyleWrapperView } from '@eeacms/volto-block-style/StyleWrapper';
import { TABS_BLOCK } from '@eeacms/volto-tabs-block/constants';
import { empty, emptyTab } from '@eeacms/volto-tabs-block/helpers';
import { setUIState } from '@plone/volto/actions/form/form';
import { BlockDataForm } from '@plone/volto/components/manage/Form';
import BlocksToolbar from '@plone/volto/components/manage/Form/BlocksToolbar';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import { getBlocksLayoutFieldname } from '@plone/volto/helpers/Blocks/Blocks';
import config from '@plone/volto/registry';
import cx from 'classnames';
import without from 'lodash/without';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DefaultEdit } from './templates/default';

const Edit = (props) => {
  const view = React.useRef(null);
  const intl = useIntl();
  const { onChangeBlock, onChangeField } = props;
  const { data = {}, block = null } = props;
  const template = data.variation || 'default';
  const tabsData = React.useMemo(() => data.data || {}, [data.data]);
  const tabsList = React.useMemo(
    () => tabsData.blocks_layout?.items || [],
    [tabsData.blocks_layout],
  );
  const tabs = tabsData.blocks || {};
  const [activeTab, setActiveTab] = React.useState(tabsList?.[0]);
  const [activeBlock, setActiveBlock] = React.useState(null);
  const [editingTab, setEditingTab] = React.useState(null);
  const [multiSelected, setMultiSelected] = React.useState([]);
  const blocksState = React.useRef({});
  const activeTabIndex = tabsList.indexOf(activeTab);
  const tabData = tabs[activeTab] || {};
  const theme = data.theme || 'light';
  const verticalAlign = data.verticalAlign || 'flex-start';
  const tabsBlockConfig = config.blocks.blocksConfig[TABS_BLOCK];

  // Redux: gridSelected para destacar bloco/tab com erro
  const gridSelected = useSelector((state) => state.form?.ui?.gridSelected);
  const dispatch = useDispatch();

  React.useEffect(() => {
    // Sem gridSelected não há o que procurar: percorrer todas as abas a cada
    // alteração trava a edição de blocos grandes.
    if (!gridSelected) return;
    if (tabsData && tabsData.blocks && tabsData.blocks_layout?.items) {
      // Procura em qual tab está o bloco com erro
      const tabWithError = tabsList.find((tabId) =>
        Object.keys(tabsData.blocks[tabId]?.blocks || {}).includes(
          gridSelected,
        ),
      );
      if (tabWithError) {
        setActiveTab(tabWithError);
        setActiveBlock(gridSelected);
      }
      // Limpa o gridSelected após usar
      dispatch(setUIState({ gridSelected: null }));
    }
  }, [gridSelected, tabsData, tabsList, dispatch]);

  const activeTemplate = config.blocks.blocksConfig[
    TABS_BLOCK
  ].variations.filter((v, _i) => v.id === template);

  const TabsEdit = activeTemplate?.[0]?.edit || DefaultEdit;

  const schemaObject = tabsBlockConfig.blockSchema(props);

  React.useEffect(() => {
    if (!Object.keys(data?.data?.blocks || {}).length) {
      // Initialize TABS_BLOCK
      const tabsData = empty({
        schema: schemaObject.properties.data.schema,
        intl,
      });
      onChangeBlock(block, {
        ...data,
        data: {
          ...tabsData,
        },
      });
      setActiveTab(tabsData.blocks_layout?.items?.[0]);
    }
    /* eslint-disable-next-line */
  }, []);

  const handleKeyDown = (
    e,
    index,
    block,
    node,
    {
      disableEnter = false,
      disableArrowUp = false,
      disableArrowDown = false,
    } = {},
  ) => {
    if (e.key === 'ArrowUp' && !disableArrowUp && !activeBlock) {
      props.onFocusPreviousBlock(block, node);
      e.preventDefault();
    }
    if (e.key === 'ArrowDown' && !disableArrowDown && !activeBlock) {
      props.onFocusNextBlock(block, node);
      e.preventDefault();
    }
    if (e.key === 'Enter' && !disableEnter && !activeBlock && !editingTab) {
      props.onAddBlock(config.settings.defaultBlockType, index + 1);
      e.preventDefault();
    }
    if (e.key === 'Enter' && editingTab) {
      setEditingTab(null);
      e.preventDefault();
    }
  };

  const onChangeTabData = (id, value) => {
    // special handling of blocks and blocks_layout
    if (['blocks', 'blocks_layout'].indexOf(id) > -1) {
      blocksState.current[id] = value;
      onChangeBlock(block, {
        ...data,
        data: {
          ...tabsData,
          blocks: {
            ...tabsData.blocks,
            [activeTab]: {
              ...tabData,
              ...blocksState.current,
            },
          },
        },
      });
    } else {
      onChangeField(id, value);
    }
  };

  const onSelectBlock = (id, isMultipleSelection, event) => {
    let newMultiSelected = [];
    let selected = id;

    if (isMultipleSelection) {
      selected = null;
      const blocksLayoutFieldName = getBlocksLayoutFieldname(tabData);

      const blocks_layout = tabData[blocksLayoutFieldName].items;

      if (event.shiftKey) {
        const anchor =
          multiSelected.length > 0
            ? blocks_layout.indexOf(multiSelected[0])
            : blocks_layout.indexOf(activeBlock);
        const focus = blocks_layout.indexOf(id);

        if (anchor === focus) {
          newMultiSelected = [id];
        } else if (focus > anchor) {
          newMultiSelected = [...blocks_layout.slice(anchor, focus + 1)];
        } else {
          newMultiSelected = [...blocks_layout.slice(focus, anchor + 1)];
        }
      }

      if ((event.ctrlKey || event.metaKey) && !event.shiftKey) {
        if (multiSelected.includes(id)) {
          selected = null;
          newMultiSelected = without(multiSelected, id);
        } else {
          newMultiSelected = [...(multiSelected || []), id];
        }
      }
    }

    setActiveBlock(selected);
    setMultiSelected(newMultiSelected);
  };

  return (
    <fieldset>
      <BlockStyleWrapperEdit {...props}>
        {/* <legend
          onClick={() => {
            setActiveBlock(null);
            props.setSidebarTab(1);
          }}
          aria-hidden="true"
        >
          {intl.formatMessage(messages.Tabs)}
        </legend> */}
        <div
          className={cx('tabs-block edit', theme, verticalAlign, template)}
          ref={view}
          role="presentation"
          onKeyDown={(e) => {
            handleKeyDown(e, props.index, props.block, props.blockNode.current);
          }}
          // The tabIndex is required for the keyboard navigation
          /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
          tabIndex={-1}
        >
          <StyleWrapperView
            {...props}
            data={tabData}
            styleData={tabData.styles || {}}
            styled={true}
          >
            <TabsEdit
              {...props}
              schema={schemaObject}
              activeBlock={activeBlock}
              activeTab={activeTab}
              activeTabIndex={activeTabIndex}
              editingTab={editingTab}
              empty={empty}
              emptyTab={emptyTab}
              metadata={props.metadata || props.properties}
              multiSelected={multiSelected}
              tabs={tabs}
              tabData={tabData}
              tabsData={tabsData}
              tabsList={tabsList}
              node={view}
              template={template}
              onChangeTabData={onChangeTabData}
              onSelectBlock={onSelectBlock}
              setActiveBlock={setActiveBlock}
              setActiveTab={setActiveTab}
              setEditingTab={setEditingTab}
            />
          </StyleWrapperView>

          {props.selected ? (
            <BlocksToolbar
              formData={tabData}
              selectedBlock={activeTab}
              selectedBlocks={multiSelected}
              onChangeBlocks={(newBlockData) => {
                onChangeBlock(block, {
                  ...data,
                  data: {
                    ...tabsData,
                    blocks: {
                      ...tabsData.blocks,
                      [activeTab]: {
                        ...tabData,
                        ...newBlockData,
                      },
                    },
                  },
                });
              }}
              onSetSelectedBlocks={(blockIds) => {
                setMultiSelected(blockIds);
              }}
              onSelectBlock={onSelectBlock}
            />
          ) : (
            ''
          )}
          {!data?.readOnlySettings && !activeBlock ? (
            <SidebarPortal selected={props.selected}>
              <BlockDataForm
                block={block}
                schema={schemaObject}
                title={schemaObject.title}
                onChangeBlock={onChangeBlock}
                onChangeField={(id, value) => {
                  onChangeBlock(block, {
                    ...data,
                    [id]: value,
                  });
                }}
                formData={data}
              />
            </SidebarPortal>
          ) : (
            ''
          )}
        </div>
      </BlockStyleWrapperEdit>
    </fieldset>
  );
};

export default Edit;
